import ProductSystemPdfImageOverride from '@/models/ProductSystemPdfImageOverride';
import ProductSystemPdfImageOverrideRepository from '@/repositories/ProductSystemPdfImageOverrideRepository';
import i18n from '@/i18n';
import { PdfImageOverride, PdfImageOverrideConfig } from '@/modules/CMS/interfaces/PdfImageOverride';

function isNonEmptyConfig(config: unknown): config is PdfImageOverrideConfig {
    if (config === null || typeof config !== 'object' || Array.isArray(config)) {
        return false;
    }

    return Object.keys(config as PdfImageOverrideConfig).length > 0;
}

export function normalizePdfImageOverrideConfig(config: unknown): PdfImageOverrideConfig {
    if (isNonEmptyConfig(config)) {
        return config;
    }

    if (!Array.isArray(config)) {
        return {};
    }

    return config.reduce((normalizedConfig: PdfImageOverrideConfig, entry: any) => {
        if (entry && typeof entry.id === 'string' && Object.prototype.hasOwnProperty.call(entry, 'value')) {
            normalizedConfig[entry.id] = entry.value;
        }
        return normalizedConfig;
    }, {});
}

export function validateProductSystemPdfImageOverrides(pdfImageOverrides: PdfImageOverride[]): void {
    for (const override of pdfImageOverrides || []) {
        if (!override.image || override.image.length === 0) {
            throw new Error(i18n.t('Each conditional PDF image needs an image.') as string);
        }
        if (override.configError || !isNonEmptyConfig(override.config)) {
            throw new Error(i18n.t('Each conditional PDF image needs at least one configuration value.') as string);
        }
    }
}

export async function saveProductSystemPdfImageOverrides(
    pdfImageOverrides: PdfImageOverride[],
    productSystemId: string,
): Promise<void> {
    validateProductSystemPdfImageOverrides(pdfImageOverrides);

    // Get existing overrides for this product system
    const existingOverrides = ProductSystemPdfImageOverrideRepository.getByProductSystemId(productSystemId);
    const existingOverrideIds = existingOverrides.map((override) => override.id);

    // Track which existing overrides we've updated
    const updatedOverrideIds: string[] = [];

    // Create or update each override
    for (const override of pdfImageOverrides) {
        const pdfOfferImageId = override.image[0].uid;

        try {
            if (override.id) {
                // Update existing override
                await ProductSystemPdfImageOverride.updateExisting(
                    override.id,
                    override.config,
                    pdfOfferImageId,
                    productSystemId,
                );
                updatedOverrideIds.push(override.id);
            } else {
                // Create new override
                const response = await ProductSystemPdfImageOverride.createNew(
                    override.config,
                    pdfOfferImageId,
                    productSystemId,
                );
                if (response && response.data && response.data.id) {
                    updatedOverrideIds.push(response.data.id);
                }
            }
        } catch (e) {
            return Promise.reject(e);
        }
    }

    // Delete overrides that were removed
    const overridesToDelete = existingOverrideIds.filter((id) => !updatedOverrideIds.includes(id));
    for (const overrideId of overridesToDelete) {
        await ProductSystemPdfImageOverride.deleteExisting(overrideId);
    }

    return Promise.resolve();
}
